import React, { useState } from 'react';
import {
  Box,
  Typography,
  BottomNavigation,
  BottomNavigationAction,
} from '@mui/material';
import {
  HomeOutlined,
  PeopleOutlined,
  BarChartOutlined,
  EventSeatOutlined,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import DemoChrome from './DemoChrome';
import DemoHomeTab from './crm/DemoHomeTab';
import DemoSessionsTab from './crm/DemoSessionsTab';
import DemoStudentsTab from './crm/DemoStudentsTab';
import DemoAnalyticsTab from './crm/DemoAnalyticsTab';

const TABS = [
  { label: 'Home', icon: <HomeOutlined /> },
  { label: 'Sessions', icon: <EventSeatOutlined /> },
  { label: 'Students', icon: <PeopleOutlined /> },
  { label: 'Analytics', icon: <BarChartOutlined /> },
];

const TAB_COMPONENTS = [
  <DemoHomeTab key="home" />,
  <DemoSessionsTab key="sessions" />,
  <DemoStudentsTab key="students" />,
  <DemoAnalyticsTab key="analytics" />,
];

const PHONE_MAX_W = 390;
const BOTTOM_NAV_H = 64;

function LibraryCrmDemo() {
  const [tab, setTab] = useState(0);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const phoneBg = isDark ? theme.palette.background.default : '#F4F2EE';
  const frameBg = isDark ? '#17223A' : '#FFFFFF';
  const frameBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.1)';

  return (
    <DemoChrome title="Library CRM">
      {/* Outer page wrapper */}
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: phoneBg,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: { xs: 2, sm: 4 },
          px: 2,
        }}
      >
        {/* Intro text above the phone frame */}
        <Box sx={{ maxWidth: PHONE_MAX_W, width: '100%', mb: 2, textAlign: 'center' }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ mb: 0.5 }}
          >
            Your library in your pocket
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage students, sessions, and revenue — all from one dashboard.
          </Typography>
        </Box>

        {/* Phone frame */}
        <Box
          sx={{
            width: '100%',
            maxWidth: PHONE_MAX_W,
            bgcolor: frameBg,
            borderRadius: '24px',
            border: `1px solid ${frameBorder}`,
            boxShadow: isDark
              ? '0 24px 64px rgba(0,0,0,0.55)'
              : '0 24px 64px rgba(27,54,93,0.14)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 580,
            position: 'relative',
          }}
        >
          {/* Simulated status bar */}
          <Box
            sx={{
              height: 28,
              bgcolor: isDark ? '#121A2B' : '#F8F8F8',
              borderBottom: `1px solid ${frameBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              flexShrink: 0,
            }}
          >
            <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'text.secondary' }}>
              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
              BookMyLibrary CRM
            </Typography>
          </Box>

          {/* Scrollable content area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 2,
              pt: 2,
              pb: `${BOTTOM_NAV_H + 8}px`,
            }}
          >
            {TAB_COMPONENTS[tab]}
          </Box>

          {/* Bottom navigation */}
          <BottomNavigation
            value={tab}
            onChange={(_, newTab) => setTab(newTab)}
            showLabels
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: BOTTOM_NAV_H,
              borderTop: `1px solid ${frameBorder}`,
              background: isDark
                ? 'linear-gradient(to right, #17223A, #121A2B)'
                : '#FFFFFF',
              '& .MuiBottomNavigationAction-root': {
                minWidth: 0,
                gap: 0.5,
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: theme.palette.brand.orange.main,
                },
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.62rem',
                fontWeight: 600,
                '&.Mui-selected': { fontSize: '0.65rem' },
              },
            }}
          >
            {TABS.map((t, i) => (
              <BottomNavigationAction
                key={t.label}
                label={t.label}
                icon={t.icon}
                aria-label={t.label}
              />
            ))}
          </BottomNavigation>
        </Box>

        {/* Footer note */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 3, textAlign: 'center' }}
        >
          All data shown is fictional and for demonstration purposes only.
        </Typography>
      </Box>
    </DemoChrome>
  );
}

export default LibraryCrmDemo;
